// Typed client for /api/lease-drafts/* — wraps the 11 endpoints from §1.5.
//
// Uses the shared `api` helper from apps/web/src/lib/api.ts for the standard
// "auth header + 401 refresh-and-retry" wrapper. The release-on-unload path
// is special-cased: it uses `fetch keepalive: true` directly because the
// `api` helper doesn't expose the keepalive option (and we want the request
// to fire even as the page unloads — §1.6).

import { api, ApiError } from './api';
import { getAccessToken, refreshAccessToken } from './authService';
import type { LeaseFormState, ApplicationGroupShape } from './leaseConfig';
import type { ReviewSummary } from './signClient';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// ---------------------------------------------------------------------------
// Wire types — must match the API response shapes.
// ---------------------------------------------------------------------------

export interface DraftLockState {
  holder: string | null;
  holderDisplay: string | null;
  holderEmail: string | null;
  acquiredAt: string | null;
  lastHeartbeatAt: string | null;
  expiresAt: string | null;
  generationLockUntil: string | null;
}

export interface LeaseDraftRecord {
  groupId: string;
  state: LeaseFormState;
  status: 'active' | 'archived';
  lock: DraftLockState;
  latestGeneratedVersionId: string | null;
  lastGeneratedInputsHash: string | null;
  createdAt: string;
  createdBy: string;
  createdByDisplay: string;
  updatedAt: string;
  updatedBy: string;
  updatedByDisplay: string;
}

export interface LeasePdfVersionRecord {
  versionId: string;
  groupId: string;
  generatedAt: string;
  generatedBy: string;
  generatedByDisplay: string;
  generatedByInitials: string;
  formStateSnapshot: LeaseFormState;
  groupSnapshot: ApplicationGroupShape;
  s3Key: string;
  s3Bucket: string;
  inputsHash: string;
  pdfSizeBytes: number;
  /** HOA default fee (cents) resolved at generate time; absent on legacy rows. */
  effectiveHoaDefaultFeeCents?: number | null;
}

/** Staff-safe kickoff state, present only when LEASE_KICKOFF_ENABLED is on. */
export type LeaseKickoffSection = 'designation' | 'animals' | 'yardCare';
export interface LeaseKickoffState {
  status: 'armed' | 'finalizing' | 'sent' | 'disarmed' | null;
  outstandingSections: LeaseKickoffSection[];
  /** WEL-170: set when the kickoff was called off because the household became
   *  a tenancy in AppFolio before the lease went out. */
  notSentReason?: 'household_became_tenants';
}

export interface DraftConflictBody {
  error: string;
  lock?: DraftLockState | null;
  draft?: LeaseDraftRecord | null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const path = (groupId: string, suffix = '') =>
  `/api/lease-drafts/${encodeURIComponent(groupId)}${suffix}`;

/**
 * GET /api/lease-drafts/:groupId — null on 404.
 *
 * The response includes `inputsHash`: the server-computed hash over the
 * draft's current state + the live group (via hashLeaseInputs). Used by the
 * useLeaseDraft hook on cold mount to drive State A/B selection (the client
 * doesn't recompute the hash locally — server is the source of truth).
 */
export async function getDraft(
  groupId: string,
): Promise<{
  draft: LeaseDraftRecord;
  group: ApplicationGroupShape | null;
  inputsHash?: string;
  /** WEL-44: present when this load detected + reconciled a property move. */
  propertyMove?: { changes: string[]; newUnitLabel?: string };
  /** Omitted entirely while the server-side kickoff feature is dormant. */
  leaseKickoffEnabled?: true;
  leaseKickoff?: LeaseKickoffState;
} | null> {
  try {
    return await api.get<{
      draft: LeaseDraftRecord;
      group: ApplicationGroupShape | null;
      inputsHash?: string;
      propertyMove?: { changes: string[]; newUnitLabel?: string };
      leaseKickoffEnabled?: true;
      leaseKickoff?: LeaseKickoffState;
    }>(path(groupId));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** POST /api/lease-drafts/:groupId — create. */
export async function createDraft(
  groupId: string,
  state?: LeaseFormState,
): Promise<{ draft: LeaseDraftRecord }> {
  return api.post(path(groupId), state !== undefined ? { state } : {});
}

/** PATCH /api/lease-drafts/:groupId — autosave; returns inputsHash. */
export async function patchDraft(
  groupId: string,
  state: LeaseFormState,
): Promise<{ draft: LeaseDraftRecord; inputsHash: string | null }> {
  // Use raw fetch to access PATCH method (api helper exposes get/post only).
  return rawJson('PATCH', path(groupId), { state });
}

/**
 * DELETE /api/lease-drafts/:groupId — discard a NEVER-GENERATED draft
 * (WEL-100 "delete/restart a draft before generating a lease"). The server
 * refuses (409) once a lease PDF exists or the group was sent for signature —
 * that's the Cancel/Void/Revise lifecycle. After a successful discard the
 * next builder open re-creates the draft fresh from live AppFolio data.
 * Idempotent: an already-gone draft returns { deleted:false }.
 */
export async function discardDraft(
  groupId: string,
): Promise<{ deleted: boolean; existed?: boolean; groupId: string }> {
  return rawJson('DELETE', path(groupId));
}

export async function getLock(groupId: string): Promise<{ lock: DraftLockState }> {
  return api.get(path(groupId, '/lock'));
}

export async function acquireLock(
  groupId: string,
  takeover?: { previousHolder: string; previousHeartbeatAt: string | null },
): Promise<{ draft: LeaseDraftRecord; lock: DraftLockState }> {
  const qs = new URLSearchParams();
  if (takeover) {
    qs.set('takeover', 'true');
    qs.set('previousHolder', takeover.previousHolder);
    if (takeover.previousHeartbeatAt !== null) {
      qs.set('previousHeartbeatAt', takeover.previousHeartbeatAt);
    }
  }
  const suffix = `/lock/acquire${qs.toString() ? `?${qs.toString()}` : ''}`;
  return api.post(path(groupId, suffix));
}

export async function heartbeatLock(
  groupId: string,
): Promise<{ draft: LeaseDraftRecord; lock: DraftLockState }> {
  return api.post(path(groupId, '/lock/heartbeat'));
}

export async function releaseLock(
  groupId: string,
): Promise<{ draft: LeaseDraftRecord | null; lock: DraftLockState | null }> {
  return api.post(path(groupId, '/lock/release'));
}

/**
 * Fire-and-forget lock release for `beforeunload`. Uses `fetch keepalive: true`
 * so the request survives page unload. `navigator.sendBeacon` can't carry the
 * Authorization header (the auth plugin would reject it as unauthenticated)
 * which is why we go through fetch instead.
 *
 * Best-effort: failures are silent. If this call drops, the lock auto-expires
 * within 120s — acceptable graceful degradation per §1.6.
 */
export async function releaseLockOnUnload(groupId: string): Promise<void> {
  try {
    const token = await getAccessToken();
    await fetch(`${API_BASE}${path(groupId, '/lock/release')}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      keepalive: true,
    });
  } catch {
    /* swallow — best-effort */
  }
}

export async function listVersions(
  groupId: string,
): Promise<{ versions: LeasePdfVersionRecord[] }> {
  return api.get(path(groupId, '/versions'));
}

/**
 * The server-computed lease status OBJECT for a single group (same vocabulary
 * as the application-list `leaseStatus`). Mirrors the backend's
 * ComputedLeaseStatus (leaseSignatures.ts).
 */
export interface ComputedLeaseStatus {
  status: string;
  label: string;
  signedCount?: number;
  totalCount?: number;
  isRevision?: boolean;
}

/**
 * GET /api/lease-drafts/:groupId/status — the LIVE per-group computed lease
 * status (reuses the same compute as GET /api/applications). Used by the
 * ConfigurePage lifecycle actions so Cancel / Cancel&resend / Revise become
 * reachable post-send (the draft record alone doesn't carry the status).
 */
export async function getLeaseStatus(
  groupId: string,
): Promise<{ status: ComputedLeaseStatus; signatureRecord: unknown | null }> {
  return api.get(path(groupId, '/status'));
}

export interface KickoffMutationResult {
  kickoff: {
    status: NonNullable<LeaseKickoffState['status']>;
    groupId?: string;
    gateSections?: LeaseKickoffSection[];
  };
  outcome?: 'armed';
  requestedSections?: LeaseKickoffSection[];
}

/** Arm the intake→lease chain. A no-gate arm finalizes inline on the server. */
export function armLeaseKickoff(groupId: string): Promise<KickoffMutationResult> {
  return api.post<KickoffMutationResult>(path(groupId, '/kickoff'), {});
}

/** Disarm an armed kickoff without changing the underlying lease draft. */
export function disarmLeaseKickoff(
  groupId: string,
): Promise<{
  changed: boolean;
  kickoff: KickoffMutationResult['kickoff'] | null;
  reason?: 'send_in_progress';
}> {
  return api.del(path(groupId, '/kickoff'));
}

/** Ask the durable finalizer to make another attempt after staff clears a gate. */
export function finalizeLeaseKickoff(
  groupId: string,
): Promise<{ kickoff: KickoffMutationResult['kickoff'] | null }> {
  return api.post(path(groupId, '/kickoff/finalize'), {});
}

export async function getVersion(
  groupId: string,
  versionId: string,
): Promise<{ version: LeasePdfVersionRecord; downloadUrl: string }> {
  return api.get(path(groupId, `/versions/${encodeURIComponent(versionId)}`));
}

/**
 * The lease Review summary shown above the builder's lease preview. Computed
 * SERVER-SIDE with the same helpers that print the PDF — never recomputed here.
 * Mirrors apps/api/src/routes/leaseReviewSummary.ts.
 */
export interface LeaseReviewSummaryResponse {
  summary: ReviewSummary;
  /** 'draft' = the saved draft as generate would print it; 'version' = a
   *  generated version's frozen snapshot. */
  source: 'draft' | 'version';
  /** Draft: when it was last saved. Version: when it was generated. */
  asOf: string | null;
  /** The HOA default fee (cents) the summary was computed with (null = none).
   *  The builder's preview renders with it so its HOA row and totals match. */
  hoaDefaultFeeCents: number | null;
  /** RRA v2.1: an accommodation request is open on this household. RRA v2.2: the advisory carries
   *  no animal names (a request no longer names an animal), so `animalNames` is absent or empty; a
   *  pre-v2.2 request may still list the names of its pending animals (names only).
   *  `animalChargesSuspended` is true while the lease suspends animal charges for the whole household.
   *  `animalRequestOpen` (RRA v2.2 part B) is true when an ANIMAL request is open — the builder then
   *  shows the `Decision pending` mark per animal; a rule or physical-change request leaves it false.
   *  `markableAnimals` is the merged roster the lease itself is built from (AppFolio pets + tenant-intake
   *  animals, exempt ones excluded): the builder ticks from it and writes each `key` as given. */
  rraRequestOpen?: { animalNames?: string[]; animalChargesSuspended?: boolean; animalRequestOpen?: boolean; markableAnimals?: Array<{ key: string; name: string; type?: 'dog' | 'cat' | 'other' }> } | null;
}

/** The saved draft, run through generate's own state preparation. */
export async function getDraftReviewSummary(groupId: string): Promise<LeaseReviewSummaryResponse> {
  return api.get(path(groupId, '/review-summary'));
}

/**
 * WEL-169: pull the household's AppFolio property (and its units) into the
 * mirror now — AppFolio sends no notice when a property custom field changes.
 * Mirrors apps/api/src/routes/leaseMirrorRefresh.ts.
 */
export interface AppFolioRefreshResult {
  refreshed: boolean;
  /** At least one mirror record changed — reload the group. */
  changed: boolean;
  refreshedAt: string | null;
  debounced?: boolean;
  /** Plain-language reason when not refreshed. */
  message?: string;
}

export async function refreshFromAppFolio(groupId: string): Promise<AppFolioRefreshResult> {
  return api.post(path(groupId, '/refresh-appfolio'), {});
}

/** One generated version, from its frozen snapshot (the printed lease). */
export async function getVersionReviewSummary(
  groupId: string,
  versionId: string,
): Promise<LeaseReviewSummaryResponse> {
  return api.get(path(groupId, `/versions/${encodeURIComponent(versionId)}/review-summary`));
}

// ---------------------------------------------------------------------------
// Signing status (ops UI) — per-group view of every DocuSeal submitter, with
// resend + change-email affordances. Mirrors the backend's signing routes.
// ---------------------------------------------------------------------------

export type SubmitterStatus =
  | 'pending'
  | 'sent'
  | 'opened'
  | 'completed'
  | 'declined';

export interface SigningSubmitter {
  /** "Resident1".."ResidentN", "Broker", "Designated Broker". */
  role: string;
  name?: string;
  email: string;
  status: SubmitterStatus;
  sentAt?: string;
  openedAt?: string;
  completedAt?: string;
  declinedAt?: string;
}

export interface SigningStatus {
  /** Derived lease status, e.g. "Waiting WHP", "Fully Executed". */
  status: string;
  cancelled?: boolean;
  /** The PDF version this submission was sent from — needed for cancel / void. */
  versionId: string;
  /** 'lease' (default/omitted) or 'adoption_addendum'. */
  documentType?: 'lease' | 'adoption_addendum';
  /** Audit reason when cancelled. 'replaced' / 'withdrawn' are condition-report
   *  corrected-version reasons (never on a lease). */
  cancelReason?: 'cancel' | 'revise' | 'auto-supersede' | 'void' | 'replaced' | 'withdrawn';
  /**
   * The canonical lease link WHP shares — the lead signer's stable SPA
   * `/sign/:uuid` page (live status + lease download). Absent only when the
   * record has no submitters to address. The signing modal surfaces this as
   * "View lease ↗" (replacing the old DocuSeal-console deep-link).
   */
  leaseLink?: string;
  submitters: SigningSubmitter[];
}

/**
 * GET /api/lease-drafts/:groupId/signing — the per-submitter signing status for
 * a group. Returns `null` on 404 (the lease was never sent for signature), so
 * the caller can distinguish "not sent yet" from a real error.
 */
export async function getSigningStatus(
  groupId: string,
): Promise<{ signing: SigningStatus } | null> {
  try {
    return await api.get<{ signing: SigningStatus }>(path(groupId, '/signing'));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * PATCH /api/lease-drafts/:groupId/signers/:role — change a submitter's email
 * (only valid while that submitter is pending/sent/opened). Returns the fresh
 * signing snapshot.
 *
 * `documentType` scopes the swap to that record: the route otherwise targets the
 * group's NEWEST signature record across ALL document types, and a real
 * application group can hold BOTH a 'lease' and an 'adoption_addendum' under the
 * same groupId. Always pass it when the caller knows which record it's showing.
 */
export async function updateSignerEmail(
  groupId: string,
  role: string,
  email: string,
  documentType?: CurrentDocumentType,
): Promise<{ signing: SigningStatus }> {
  return rawJson(
    'PATCH',
    path(groupId, `/signers/${encodeURIComponent(role)}`),
    { email, ...(documentType ? { documentType } : {}) },
  );
}

/**
 * POST /api/lease-drafts/:groupId/signers/:role/resend — re-send the signing
 * invitation to a submitter. Returns the fresh signing snapshot.
 */
export async function resendSigner(
  groupId: string,
  role: string,
): Promise<{ signing: SigningStatus }> {
  return api.post(path(groupId, `/signers/${encodeURIComponent(role)}/resend`));
}

export async function generate(
  groupId: string,
): Promise<{ version: LeasePdfVersionRecord; downloadUrl: string }> {
  return api.post(path(groupId, '/generate'));
}

// ---------------------------------------------------------------------------
// Signed-document download (doc management) — once a lease is Fully Executed,
// the combined signed PDF + the audit certificate are available as presigned
// download URLs. Both return 404 (→ ApiError) when the document hasn't been
// stored yet, so callers can hide the button when the fetch fails.
// ---------------------------------------------------------------------------

/**
 * GET /api/lease-drafts/:groupId/signed-pdf — a 7-day presigned URL for the
 * combined signed lease PDF. Throws ApiError(404) when the signed PDF isn't
 * available (lease not executed, or not yet stored).
 */
export async function getSignedPdfUrl(groupId: string): Promise<{ url: string }> {
  return api.get(path(groupId, '/signed-pdf'));
}

/**
 * GET /api/lease-drafts/:groupId/audit-cert — a 7-day presigned URL for the
 * signing audit certificate. Throws ApiError(404) when it isn't available.
 */
export async function getAuditCertUrl(groupId: string): Promise<{ url: string }> {
  return api.get(path(groupId, '/audit-cert'));
}

// ---------------------------------------------------------------------------
// Current-state document download — the unified staff endpoint. Works for
// EVERY signature-backed document type at any point in its life: executed →
// the signed PDF; mid-signing → the live DocuSeal combined PDF (marks so far);
// generated-but-never-sent lease → the newest generated version.
// ---------------------------------------------------------------------------

/** Wire value for a signature record's documentType (mirrors the API's
 *  LeaseDocumentTypeWire). */
export type CurrentDocumentType =
  | 'lease'
  | 'adoption_addendum'
  | 'condition_report'
  | 'moveout_funds_direction'
  | 'payment_plan'
  | 'manual_document'
  | 'property_management_agreement'
  | 'leaseholder_change'
  | 'leaseholder_removal'
  | 'animal_addendum'
  | 'animal_addendum_paid';

export interface CurrentDocumentUrl {
  url: string;
  filename: string;
  source: 'executed' | 'docuseal' | 'generated';
}

function currentDocumentPath(
  groupId: string,
  documentType: CurrentDocumentType | undefined,
  probe: boolean,
): string {
  const q = new URLSearchParams();
  if (documentType) q.set('documentType', documentType);
  if (probe) q.set('probe', '1');
  const qs = q.toString();
  return path(groupId, `/current-document${qs ? `?${qs}` : ''}`);
}

/**
 * GET /api/lease-drafts/:groupId/current-document — a time-limited URL for the
 * document in its CURRENT state. Throws ApiError(404) when no document exists
 * yet (body.reason: 'never_generated' | 'not_available').
 */
export async function getCurrentDocumentUrl(
  groupId: string,
  documentType?: CurrentDocumentType,
): Promise<CurrentDocumentUrl> {
  return api.get(currentDocumentPath(groupId, documentType, false));
}

/**
 * Cheap availability check (?probe=1) — no DocuSeal round-trip server-side, so
 * it's safe to call on every detail-view open.
 */
export async function probeCurrentDocument(
  groupId: string,
  documentType?: CurrentDocumentType,
): Promise<{ available: boolean; reason?: string }> {
  return api.get(currentDocumentPath(groupId, documentType, true));
}

// ---------------------------------------------------------------------------
// Signing lifecycle actions (Cancel / Void / Revise) — wired from the signing
// status modal. Three distinct outcomes:
//   - Cancel  → terminal. GROUP-level revise(action:'cancel') archives ALL open
//     submissions and marks the lease Cancelled. (Group-level, not version, so
//     a hold-and-send older-open submission can't stay live and signable.)
//   - Void    → archive the active request but KEEP the same PDF; the lease
//     reverts to "Lease Generated" so staff can re-send WITHOUT editing.
//   - Revise  → GROUP-level revise(action:'cancel-resend'): archive everything
//     and reopen the lease as an editable draft (Cancelled – Resume Draft,
//     regenerate:true) so staff change terms, regenerate, then resend. Dovetails
//     with the existing replace/auto-cancel flow.
// ---------------------------------------------------------------------------

/** Result of POST …/versions/:versionId/void. */
export interface VoidLeaseResult {
  voided: boolean;
  hadSubmission: boolean;
  status: string;
  submissionId?: number;
  alreadyClosed?: boolean;
}

/**
 * POST /api/lease-drafts/:groupId/versions/:versionId/void — cancel the active
 * signature request but keep the generated lease (→ "Lease Generated", re-sendable).
 */
export async function voidLeaseVersion(
  groupId: string,
  versionId: string,
): Promise<VoidLeaseResult> {
  return api.post(path(groupId, `/versions/${encodeURIComponent(versionId)}/void`));
}

/** The group-level revise actions the backend accepts. */
export type ReviseAction = 'cancel' | 'cancel-resend' | 'keep-open';

/** Result of POST /api/lease-drafts/:groupId/revise. */
export interface ReviseLeaseResult {
  action: ReviseAction;
  regenerate: boolean;
  keepOpen: boolean;
  cancelledSubmissionIds: number[];
  keptOpenSubmissionId?: number;
  status: string;
  hadSubmission?: boolean;
}

/**
 * POST /api/lease-drafts/:groupId/revise — GROUP-level. Used by the modal's
 * Cancel ('cancel' → terminal Cancelled) and Revise ('cancel-resend' →
 * Cancelled – Resume Draft, regenerate:true) actions.
 */
export async function reviseLease(
  groupId: string,
  action: ReviseAction,
): Promise<ReviseLeaseResult> {
  return api.post(path(groupId, '/revise'), { action });
}

// ---------------------------------------------------------------------------
// Internal: raw JSON with auth + 401 retry, used for PATCH.
// ---------------------------------------------------------------------------

async function rawJson<T>(method: string, p: string, body?: unknown): Promise<T> {
  const url = p.startsWith('http') ? p : `${API_BASE}${p}`;
  const token = await getAccessToken();
  const init: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  let res = await fetch(url, init);
  if (res.status === 401) {
    if (await refreshAccessToken()) {
      const t2 = await getAccessToken();
      const init2 = {
        ...init,
        headers: {
          ...init.headers,
          ...(t2 ? { Authorization: `Bearer ${t2}` } : {}),
        },
      };
      res = await fetch(url, init2);
    }
  }
  if (!res.ok) {
    let bodyText: unknown = null;
    try {
      bodyText = await res.json();
    } catch {
      /* empty */
    }
    throw new ApiError(res.status, bodyText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
